// Twix.js (https://github.com/icambron/twix.js), decaffeinated
import moment, { type Moment } from "moment";

interface TwixOptions {
	allDay?: boolean;
	parseFormat?: string;
	parseStrict?: boolean;
}

interface FormatOptions {
	groupMeridiems: boolean;
	spaceBeforeMeridiem: boolean;
	spaceBeforeMonth: boolean;
	spaceBeforeDay: boolean;
	showDayOfWeek: boolean;
	showYearFirst: boolean;
	hideTime: boolean;
	hideYear: boolean;
	hideDate: boolean;
	implicitMinutes: boolean;
	implicitDate: boolean;
	implicitYear: boolean;
	yearFormat: string;
	monthFormat: string;
	weekdayFormat: string;
	dayFormat: string;
	meridiemFormat: string;
	hourFormat: string;
	minuteFormat: string;
	allDay: string;
	explicitAllDay: boolean;
	lastNightEndsAt: number;
	template: (left: string, right: string) => string;
}

export class Twix {
	oStart: Moment;
	lastMilli: Moment;
	oEnd: Moment;
	displayEnd: Moment;
	mStart: Moment;
	mEnd: Moment;
	allDay: boolean;

	static formatTemplate = (leftSide: string, rightSide: string) => `${leftSide} - ${rightSide}`;

	constructor(start: Moment, end: Moment, options: TwixOptions = {}) {
		this.oStart = moment(start, options.parseFormat, options.parseStrict);
		this.oEnd = moment(end, options.parseFormat, options.parseStrict);
		this.mStart = this.oStart;
		this.lastMilli = this.oStart;
		this.mEnd = this.oEnd;
		this.displayEnd = this.mEnd;

		this.allDay = options.allDay != null ? options.allDay : false;

		this.mutated();
	}

	// -- INFORMATIONAL --
	start() {
		return this.mStart.clone();
	}

	end() {
		return this.mEnd.clone();
	}

	isSame(period: moment.unitOfTime.StartOf) {
		return this.mStart.isSame(this.mEnd, period);
	}

	length(period?: moment.unitOfTime.Diff, floatingPoint?: boolean) {
		return this.displayEnd.diff(this.mStart, period, floatingPoint);
	}

	count(period: moment.unitOfTime.Diff) {
		const start = this.start().startOf(period);
		const end = this.end().startOf(period);
		return end.diff(start, period) + 1;
	}

	countInner(period: moment.unitOfTime.Base) {
		const [start, end] = this._inner(period);

		if (start >= end) {
			return 0;
		}
		return end.diff(start, period);
	}

	iterate(intervalAmount: number, period: moment.unitOfTime.Base = "ms", minHours = 0) {
		const start = this.start().startOf(period);
		let end = this.end().startOf(period);
		if (this.allDay) {
			end = end.add(1, "d");
		}
		const hasNext = () =>
			(!this.allDay && start <= end && (!minHours || !start.isSame(end) || this.mEnd.hours() > minHours)) ||
			(this.allDay && start < end);

		return this._iterateHelper(period, start, hasNext, intervalAmount);
	}

	humanizeLength() {
		if (this.allDay) {
			if (this.isSame("d")) {
				return "all day";
			} else {
				return this.mStart.from(this.end().add(1, "d"), true);
			}
		} else {
			return this.mStart.from(this.mEnd, true);
		}
	}

	asDuration() {
		const diff = this.mEnd.diff(this.mStart);
		return moment.duration(diff);
	}

	isPast() {
		return this.lastMilli < moment();
	}

	isFuture() {
		return this.mStart > moment();
	}

	isCurrent() {
		return !this.isPast() && !this.isFuture();
	}

	contains(mom: Moment) {
		return this.mStart <= mom && this.lastMilli >= mom;
	}

	isEmpty() {
		return this.mStart.isSame(this.displayEnd);
	}

	// -- WORK WITH MULTIPLE RANGES --
	overlaps(other: Twix) {
		return this.displayEnd.isAfter(other.mStart) && this.mStart.isBefore(other.displayEnd);
	}

	engulfs(other: Twix) {
		return this.mStart <= other.mStart && this.displayEnd >= other.displayEnd;
	}

	union(other: Twix) {
		const allDay = this.allDay && other.allDay;
		const newStart = this.mStart < other.mStart ? this.mStart : other.mStart;
		const newEnd =
			this.lastMilli > other.lastMilli
				? allDay
					? this.mEnd
					: this.displayEnd
				: allDay
					? other.mEnd
					: other.displayEnd;
		return new Twix(newStart, newEnd, { allDay });
	}

	intersection(other: Twix) {
		const allDay = this.allDay && other.allDay;
		const newStart = this.mStart > other.mStart ? this.mStart : other.mStart;
		const newEnd =
			this.lastMilli < other.lastMilli
				? allDay
					? this.mEnd
					: this.displayEnd
				: allDay
					? other.mEnd
					: other.displayEnd;

		return new Twix(newStart, newEnd, { allDay });
	}

	xor(...others: Twix[]) {
		let open = 0;
		let start = null;
		const results: Twix[] = [];

		const allDay = Array.from(others).filter(o => o.allDay).length === others.length;

		let arr = [];
		const iterable = ([this] as Twix[]).concat(others);
		for (let i = 0; i < iterable.length; i++) {
			const item = iterable[i];
			arr.push({ time: item.mStart, i, type: 0 });
			arr.push({ time: item.displayEnd, i, type: 1 });
		}
		arr = arr.sort((a, b) => a.time.valueOf() - b.time.valueOf());

		for (let other of arr) {
			if (other.type === 1) {
				open -= 1;
			}
			if (open === other.type) {
				start = other.time;
			}
			if (open === (other.type + 1) % 2) {
				if (start) {
					const last = results[results.length - 1];
					if (last && last.mEnd.isSame(start)) {
						last.oEnd = other.time;
						last.mutated();
					} else {
						//because we used the diffable end, we have to subtract back off a day. blech
						const endTime = allDay ? other.time.clone().subtract(1, "d") : other.time;
						const t = new Twix(start, endTime, { allDay });
						if (!t.isEmpty()) {
							results.push(t);
						}
					}
				}
				start = null;
			}
			if (other.type === 0) {
				open += 1;
			}
		}
		return results;
	}

	difference(...others: Twix[]) {
		return (() => {
			const result = [];
			for (let t of Array.from(this.xor(...others).map(i => this.intersection(i)))) {
				if (!t.isEmpty() && t.isValid()) {
					result.push(t);
				}
			}
			return result;
		})();
	}

	// split(arg: moment.Duration | Moment[]) {
	// 	let dur, start, times;
	// 	let end = (start = this.start());

	// 	if (moment.isDuration(args[0])) {
	// 		dur = args[0];
	// 	} else if (
	// 		(!moment.isMoment(args[0]) && !isArray(args[0]) && typeof args[0] === "object") ||
	// 		(typeof args[0] === "number" && typeof args[1] === "string")
	// 	) {
	// 		dur = moment.duration(args[0], args[1]);
	// 	} else if (isArray(args[0])) {
	// 		times = args[0];
	// 	} else {
	// 		times = args;
	// 	}

	// 	if (times) {
	// 		times = Array.from(times).map(time => moment(time));
	// 		times = (() => {
	// 			const result = [];
	// 			for (let mom of Array.from(times)) {
	// 				if (mom.isValid() && mom >= start) {
	// 					result.push(mom);
	// 				}
	// 			}
	// 			return result;
	// 		})().sort((a, b) => a.valueOf() - b.valueOf());
	// 	}

	// 	if ((dur && dur.asMilliseconds() === 0) || (times && times.length === 0)) {
	// 		return [this];
	// 	}

	// 	const vals = [];
	// 	let i = 0;
	// 	const final = this.displayEnd;
	// 	while (start < final && (times == null || times[i])) {
	// 		end = dur ? start.clone().add(dur) : times[i].clone();
	// 		end = moment.min(final, end);
	// 		if (!start.isSame(end)) {
	// 			vals.push(new Twix(start, end));
	// 		}
	// 		start = end;
	// 		i += 1;
	// 	}
	// 	if (!end.isSame(this.displayEnd) && times) {
	// 		vals.push(new Twix(end, this.displayEnd));
	// 	}
	// 	return vals;
	// }

	// divide(parts) {
	// 	return this.split(this.length() / parts, "ms").slice(0, +(parts - 1) + 1 || undefined);
	// }

	isValid() {
		return this.mStart.isValid() && this.mEnd.isValid() && this.mStart <= this.displayEnd;
	}

	equals(other: Twix) {
		return (
			other instanceof Twix &&
			this.allDay === other.allDay &&
			this.mStart.valueOf() === other.mStart.valueOf() &&
			this.mEnd.valueOf() === other.mEnd.valueOf()
		);
	}

	// -- FORMATTING --
	toString() {
		return `{start: ${this.mStart.format()}, end: ${this.mEnd.format()}, allDay: ${this.allDay ? "true" : "false"}}`;
	}

	toArray(intervalAmount: number, period?: moment.unitOfTime.Base, minHours?: number) {
		const itr = this.iterate(intervalAmount, period, minHours);
		const range = [];
		while (itr.hasNext()) {
			range.push(itr.next());
		}
		return range;
	}

	simpleFormat(momentOpts?: string, inopts?: Partial<FormatOptions>) {
		const options = {
			allDay: "(all day)",
			template: Twix.formatTemplate,
			...inopts,
		};

		let s = options.template(this.mStart.format(momentOpts), this.mEnd.format(momentOpts));
		if (this.allDay && options.allDay) {
			s += ` ${options.allDay}`;
		}
		return s;
	}

	format(inopts?: Partial<FormatOptions>) {
		let format;
		if (this.isEmpty()) {
			return "";
		}

		const momentHourFormat = this.mStart.localeData().longDateFormat("LT")[0];

		const options = {
			groupMeridiems: true,
			spaceBeforeMeridiem: true,
			spaceBeforeMonth: true,
			spaceBeforeDay: true,
			showDayOfWeek: false,
			showYearFirst: false,
			hideTime: false,
			hideYear: false,
			hideDate: false,
			implicitMinutes: true,
			implicitDate: false,
			implicitYear: true,
			yearFormat: "YYYY",
			monthFormat: "MMM",
			weekdayFormat: "ddd",
			dayFormat: "D",
			meridiemFormat: "A",
			hourFormat: momentHourFormat,
			minuteFormat: "mm",
			allDay: "all day",
			explicitAllDay: false,
			lastNightEndsAt: 0,
			template: Twix.formatTemplate,
			...inopts,
		};

		interface FS {
			name: string;
			fn: (v: Moment) => string;
			slot: number;
			pre: string;
			ignoreEnd?: () => boolean;
		}

		const fs: FS[] = [];

		const needsMeridiem = options.hourFormat && options.hourFormat[0] === "h";

		const localFormat = this.mStart.localeData().longDateFormat("L");
		const americanish = localFormat.indexOf("M") < localFormat.indexOf("D");

		const goesIntoTheMorning =
			options.lastNightEndsAt > 0 &&
			!this.allDay &&
			this.end().startOf("d").valueOf() === this.start().add(1, "d").startOf("d").valueOf() &&
			this.mStart.hours() > 12 &&
			this.mEnd.hours() < options.lastNightEndsAt;

		const needDate =
			!options.hideDate &&
			(!options.implicitDate ||
				this.start().startOf("d").valueOf() !== moment().startOf("d").valueOf() ||
				!(this.isSame("d") || goesIntoTheMorning));

		const atomicMonthDate = !(this.allDay || options.hideTime);

		if (this.allDay && this.isSame("d") && (options.implicitDate || options.explicitAllDay)) {
			fs.push({
				name: "all day simple",
				fn() {
					return options.allDay;
				},
				pre: " ",
				slot: 0,
			});
		}

		if (
			needDate &&
			!options.hideYear &&
			(!options.implicitYear || this.mStart.year() !== moment().year() || !this.isSame("y"))
		) {
			fs.push({
				name: "year",
				fn(date: Moment) {
					return date.format(options.yearFormat);
				},
				pre: americanish ? ", " : " ",
				slot: options.showYearFirst ? -1 : 4,
			});
		}

		if (atomicMonthDate && needDate) {
			fs.push({
				name: "month-date",
				fn(date: Moment) {
					const format = americanish
						? `${options.monthFormat} ${options.dayFormat}`
						: `${options.dayFormat} ${options.monthFormat}`;
					return date.format(format);
				},
				ignoreEnd() {
					return goesIntoTheMorning;
				},
				pre: " ",
				slot: 2,
			});
		}

		if (!atomicMonthDate && needDate) {
			fs.push({
				name: "month",
				fn(date: Moment) {
					return date.format(options.monthFormat);
				},
				pre: options.spaceBeforeMonth ? " " : "",
				slot: americanish ? 2 : 3,
			});
		}

		if (!atomicMonthDate && needDate) {
			fs.push({
				name: "date",
				fn(date: Moment) {
					return date.format(options.dayFormat);
				},
				pre: options.spaceBeforeDay ? " " : "",
				slot: americanish ? 3 : 2,
			});
		}

		if (needDate && options.showDayOfWeek) {
			fs.push({
				name: "day of week",
				fn(date: Moment) {
					return date.format(options.weekdayFormat);
				},
				pre: " ",
				slot: 1,
			});
		}

		if (options.groupMeridiems && needsMeridiem && !this.allDay && !options.hideTime) {
			fs.push({
				name: "meridiem",
				fn(t: Moment) {
					return t.format(options.meridiemFormat);
				},
				slot: 6,
				pre: options.spaceBeforeMeridiem ? " " : "",
			});
		}

		if (!this.allDay && !options.hideTime) {
			fs.push({
				name: "time",
				fn(date: Moment) {
					let str =
						date.minutes() === 0 && options.implicitMinutes && needsMeridiem
							? date.format(options.hourFormat)
							: date.format(`${options.hourFormat}:${options.minuteFormat}`);

					if (!options.groupMeridiems && needsMeridiem) {
						if (options.spaceBeforeMeridiem) {
							str += " ";
						}
						str += date.format(options.meridiemFormat);
					}
					return str;
				},
				slot: 5,
				pre: ", ",
			});
		}

		interface FT {
			format: { slot: number; pre: string };
			value: () => string;
		}

		const startBucket: FT[] = [];
		const endBucket: FT[] = [];
		const commonBucket: FT[] = [];
		let together = true;

		const process = (format: FS) => {
			const startStr = format.fn(this.mStart);

			const endStr = format.ignoreEnd && format.ignoreEnd() ? startStr : format.fn(this.mEnd);

			const startGroup = {
				format,
				value() {
					return startStr;
				},
			};

			if (endStr === startStr && together) {
				return commonBucket.push(startGroup);
			} else {
				if (together) {
					together = false;
					commonBucket.push({
						format: { slot: format.slot, pre: "" },
						value() {
							return options.template(fold(startBucket), fold(endBucket, true).trim());
						},
					});
				}

				startBucket.push(startGroup);
				return endBucket.push({
					format,
					value() {
						return endStr;
					},
				});
			}
		};

		for (format of Array.from(fs)) {
			process(format);
		}

		let globalFirst = true;
		let fold = function (array: FT[], skipPre = false) {
			let localFirst = true;
			let str = "";
			for (let section of Array.from(array.sort((a, b) => a.format.slot - b.format.slot))) {
				if (!globalFirst) {
					if (localFirst && skipPre) {
						str += " ";
					} else {
						str += section.format.pre;
					}
				}

				str += section.value();

				globalFirst = false;
				localFirst = false;
			}
			return str;
		};

		return fold(commonBucket);
	}

	// -- INTERNAL --
	_iterateHelper(period: moment.unitOfTime.Base, iter: Moment, hasNext: () => boolean, intervalAmount: number) {
		return {
			next() {
				if (!hasNext()) {
					return null;
				} else {
					const val = iter.clone();
					iter.add(intervalAmount, period);
					return val;
				}
			},
			hasNext,
		};
	}

	_inner(period: moment.unitOfTime.Base = "ms", intervalAmount = 1) {
		const start = this.start();
		const end = this.displayEnd.clone();

		if (start > start.clone().startOf(period)) {
			start.startOf(period).add(intervalAmount, period);
		}
		if (end < end.clone().endOf(period)) {
			end.startOf(period);
		}

		const durationPeriod = new Twix(start, end).asDuration();
		const durationCount = durationPeriod.get(period);

		const modulus = durationCount % intervalAmount;

		end.subtract(modulus, period);

		return [start, end];
	}

	mutated() {
		this.mStart = this.allDay ? this.oStart.clone().startOf("d") : this.oStart;
		this.lastMilli = this.allDay ? this.oEnd.clone().endOf("d") : this.oEnd;
		this.mEnd = this.allDay ? this.oEnd.clone().startOf("d") : this.oEnd;
		this.displayEnd = this.allDay ? this.mEnd.clone().add(1, "d") : this.mEnd;
	}
}
